import React, { useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { UNITS } from "../lib/constants";
import { X, Plus, Trash, Gauge } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Modal: generate a Gate Pass from a source document (Tax Invoice / Repair Invoice / Delivery Challan / Job Work Challan).
 * Gate pass only needs: line items (description + qty + unit, no price), vehicle, driver, transporter, purpose.
 * Source doc_number is carried as Reference automatically via the backend /convert endpoint.
 */
let _gpSeq = 0;
const newGpUid = () => `gp-${++_gpSeq}-${Date.now()}`;

export default function GenerateGatePassModal({ source, onClose, onConverted }) {
  const [items, setItems] = useState(
    (source.items || []).map(it => ({
      _uid: newGpUid(),
      name: it.name || "",
      description: it.description || "",
      hsn: it.hsn || "",
      unit: it.unit || "NOS",
      quantity: Number(it.quantity || 0),
      // Carry rate / discount / gst forward from the source so the Gate Pass
      // also displays the value of goods (Total / Grand Total row on print).
      rate: Number(it.rate || 0),
      discount_pct: Number(it.discount_pct || 0),
      gst_rate: Number(it.gst_rate || 0),
    }))
  );
  const [driverName, setDriverName] = useState(source.driver_name || "");
  const [vehicleNo, setVehicleNo] = useState(source.vehicle_no || "");
  const [transporterName, setTransporterName] = useState(source.transporter_name || "");
  const [purpose, setPurpose] = useState(
    source.doc_type === "job_work_challan" ? "Returnable" : "Outward"
  );
  const [docDate, setDocDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const setItem = (i, patch) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const addRow = () => setItems(prev => [...prev, { _uid: newGpUid(), name: "", description: "", hsn: "", unit: "NOS", quantity: 1, rate: 0, discount_pct: 0, gst_rate: 0 }]);
  const removeRow = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));

  const confirm = async () => {
    if (items.length === 0 || items.every(i => !i.name)) return toast.error("At least one line item is required");
    if (!vehicleNo) return toast.error("Vehicle number is required on a Gate Pass");
    setSaving(true);
    try {
      const { data } = await api.post(`/documents/${source.id}/convert`, {
        target: "gate_pass",
        edits: {
          doc_date: docDate,
          notes,
          driver_name: driverName,
          gate_pass_purpose: purpose,
          vehicle_no: vehicleNo,
          transporter_name: transporterName,
          items: items.filter(i => i.name).map(i => ({
            ...i,
            quantity: Number(i.quantity || 0),
            rate: Number(i.rate || 0),
            discount_pct: Number(i.discount_pct || 0),
            gst_rate: Number(i.gst_rate || 0),
          })),
        },
      });
      toast.success(`Gate Pass created · ${data.doc_number}`);
      onConverted?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="gate-pass-modal">
      <div className="bg-white rounded-lg border border-border max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <div className="flex items-center gap-2 font-display text-lg font-semibold"><Gauge size={18} weight="duotone" className="text-brand" /> Generate Gate Pass</div>
            <div className="text-xs text-ink-muted mt-0.5">
              From <span className="font-mono font-semibold">{source.doc_number}</span> · {source.party_snapshot?.name} · Confirm vehicle, driver &amp; items before creating the gate pass.
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="close-gate-pass-modal"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-auto p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><div className="label-overline block mb-1.5">Gate Pass Date</div><input type="date" className="field" value={docDate} onChange={e => setDocDate(e.target.value)} data-testid="gp-date" /></div>
            <div>
              <div className="label-overline block mb-1.5">Purpose</div>
              <select className="field" value={purpose} onChange={e => setPurpose(e.target.value)} data-testid="gp-purpose">
                <option>Outward</option><option>Inward</option><option>Returnable</option>
              </select>
            </div>
            <div><div className="label-overline block mb-1.5">Vehicle No *</div><input className="field font-mono uppercase" value={vehicleNo} onChange={e => setVehicleNo(e.target.value.toUpperCase())} data-testid="gp-vehicle" /></div>
            <div><div className="label-overline block mb-1.5">Driver Name</div><input className="field" value={driverName} onChange={e => setDriverName(e.target.value)} data-testid="gp-driver" /></div>
            <div className="md:col-span-2"><div className="label-overline block mb-1.5">Transporter</div><input className="field" value={transporterName} onChange={e => setTransporterName(e.target.value)} data-testid="gp-transporter" /></div>
          </div>

          <div className="card-base overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-border">
              <div className="font-display font-semibold text-sm">Items (description, quantity &amp; rate — rates carry over from source so the Gate Pass shows the value of goods)</div>
              <button onClick={addRow} className="btn-secondary inline-flex items-center gap-1 text-xs" data-testid="gp-add-row"><Plus size={12} weight="bold" /> Add Row</button>
            </div>
            <div className="overflow-x-auto">
              <table className="table-base min-w-[760px]">
                <thead><tr>
                  <th className="w-8">#</th>
                  <th>Item</th>
                  <th>HSN</th>
                  <th>Unit</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Rate (₹)</th>
                  <th></th>
                </tr></thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={it._uid || `gp-row-${i}`} data-testid={`gp-row-${i}`}>
                      <td className="text-ink-muted">{i + 1}</td>
                      <td>
                        <input className="field" value={it.name} onChange={e => setItem(i, { name: e.target.value })} data-testid={`gp-name-${i}`} />
                        {it.description && <div className="text-[11px] text-ink-muted mt-0.5">{it.description}</div>}
                      </td>
                      <td><input className="field font-mono" style={{ width: 90 }} value={it.hsn} onChange={e => setItem(i, { hsn: e.target.value })} /></td>
                      <td>
                        <select className="field" style={{ width: 80 }} value={it.unit} onChange={e => setItem(i, { unit: e.target.value })}>
                          {UNITS.map(u => <option key={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="text-right"><input type="number" step="1" min="0" className="field text-right" style={{ width: 90 }} value={it.quantity} onChange={e => setItem(i, { quantity: e.target.value })} data-testid={`gp-qty-${i}`} /></td>
                      <td className="text-right"><input type="number" step="0.01" min="0" className="field text-right" style={{ width: 110 }} value={it.rate} onChange={e => setItem(i, { rate: e.target.value })} data-testid={`gp-rate-${i}`} /></td>
                      <td><button onClick={() => removeRow(i)} className="btn-ghost text-destructive" data-testid={`gp-remove-${i}`}><Trash size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div><div className="label-overline block mb-1.5">Notes (optional)</div><textarea rows={2} className="field" value={notes} onChange={e => setNotes(e.target.value)} data-testid="gp-notes" /></div>
        </div>

        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-border bg-surface-muted">
          <div className="text-xs text-ink-muted">Source will remain unchanged; a new Gate Pass will be created with a fresh number &amp; linked to this document.</div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="btn-secondary" data-testid="gp-cancel">Cancel</button>
            <button onClick={confirm} disabled={saving} className="btn-primary inline-flex items-center gap-1" data-testid="gp-confirm">
              <Gauge size={14} weight="bold" /> {saving ? "Creating…" : "Create Gate Pass"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
